import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { trackLoginClick, trackSignupComplete, trackSignupFree } from '../utils/analytics.js';
import { setPageSEO } from '../utils/seo.js';
import './Pricing.css';

function Pricing() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  useEffect(() => setPageSEO({
    title: 'Clientalio Pricing - Start Building Trust Free',
    description: 'Choose a Clientalio plan that helps you turn customer feedback into trust, social proof, and better website conversions.',
    canonical: 'https://clientalio.com/pricing',
    ogTitle: 'Clientalio Pricing - Start Building Trust Free',
    ogDescription: 'Start free and upgrade when customer proof becomes your growth channel.',
    twitterTitle: 'Clientalio Pricing - Start Building Trust Free',
    twitterDescription: 'Turn customer feedback into trust, social proof, and better website conversions.'
  }), []);
  
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      
      // Get user's IP address
      let ipAddress = null;
      try {
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipResponse.json();
        ipAddress = ipData.ip;
      } catch (ipError) {
        console.error('Error fetching IP address:', ipError);
      }
      
      // Build API endpoint based on whether IP was fetched successfully
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://apiclientalio.azurewebsites.net';
      const endpoint = ipAddress 
        ? `${baseUrl}/api/v1/Services/Plans/${ipAddress}` 
        : `${baseUrl}/api/v1/Services/Plans`;
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Transform API data to match our component structure
      if (!data || !Array.isArray(data)) {
        throw new Error('Invalid API response format');
      }

      const transformedPlans = data.map(item => {
        const subscription = item;
        
        
        // Extract and process appFeatures
        const appFeatures = subscription.appFeatures || [];
        const processedFeatures = appFeatures
          .filter(f => f !== null && f !== undefined && f.isHidden !== true)
          .sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0))
          .map(f => ({
            name: f.featureName || '',
            isAvailable: f.isAvailable !== false
          }));

        return {
          id: subscription.id,
          name: subscription.planName,
          description: subscription.planDescription,
          monthlyPrice: subscription.price || 0,
          yearlyPrice: subscription.discountedPrice || 0,
          features: processedFeatures
        };
      });

      setPlans(transformedPlans);
      setError(null);
    } catch (err) {
      console.error('Error fetching plans:', err);
      // Set fallback data based on the screenshot
      const fallbackPlans = [
        {
          id: 1,
          name: "Basic",
          monthlyPrice: 0,
          yearlyPrice: 0,
          features: []
        },
        {
          id: 2,
          name: "Starter",
          monthlyPrice: 19,
          yearlyPrice: 180,
          features: []
        },
        {
          id: 3,
          name: "Growth",
          monthlyPrice: 49,
          yearlyPrice: 480,
          features: []
        }
      ];
      setPlans(fallbackPlans);
      setError(null); // Don't show error if we have fallback data
    } finally {
      setLoading(false);
    }
  };

  const faqs = [
    {
      question: "Can I change plans later?",
      answer: "Yes. Start free today, then upgrade when you are ready to turn more customer feedback into growth assets."
    },
    {
      question: "Is there a free plan?",
      answer: "Yes. You can start free without a credit card and create your first branded trust page quickly."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept major credit cards including Visa, MasterCard, and American Express."
    },
    {
      question: "Can I cancel anytime?",
      answer: "Yes. You can cancel anytime with no cancellation fees."
    }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSignup = (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setEmailError('Please enter your email address');
      return;
    }
    
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    setEmailError('');
    trackSignupFree('pricing_cta_form');
    window.location.href = `/signup?email=${encodeURIComponent(email)}`;
  };

  const handlePlanSignupClick = (location) => {
    trackSignupFree(location);
    trackSignupComplete(location);
  };

  return (
    <div className="pricing-page">
      <section className="hero-section" style={{ padding: "30px 0" }}>

      </section>

      <section className="pricing-plans">
        <div className="container">
          <h3 className="section-badge">START FREE TODAY</h3>
          <h2 className="section-title"><span className="highlight">Choose how fast you want to turn customer love into growth</span></h2>

          <div className="billing-toggle-wrapper">
            <div className="billing-toggle">
              <button 
                className={billingPeriod === 'monthly' ? 'active' : ''}
                onClick={() => setBillingPeriod('monthly')}
              >
                Monthly
              </button>
              <button 
                className={billingPeriod === 'yearly' ? 'active' : ''}
                onClick={() => setBillingPeriod('yearly')}
              >
                Annually <span className="save-badge">Save {(() => {
                  // Calculate the maximum savings percentage from all paid plans
                  const savings = plans
                    .filter(plan => plan.monthlyPrice > 0 && plan.yearlyPrice > 0)
                    .map(plan => {
                      const monthlyTotal = plan.monthlyPrice * 12;
                      const savings = ((monthlyTotal - plan.yearlyPrice) / monthlyTotal) * 100;
                      return Math.round(savings);
                    });
                  return savings.length > 0 ? Math.max(...savings) : 17;
                })()}%</span>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="pricing-loading">
              <div className="spinner"></div>
              <p>Loading plans...</p>
            </div>
          ) : error ? (
            <div className="pricing-error">
              <p>Error loading pricing plans: {error}</p>
              <button onClick={fetchPlans} className="btn-primary">Retry</button>
            </div>
          ) : (
            <div className="plans-grid">
              {plans.map((plan, index) => {
                const isBasic = plan.name === 'Basic';
                const isStarter = plan.name === 'Starter';
                const isGrowth = plan.name === 'Growth';

                return (
                  <div
                    key={plan.id || index}
                    className={`plan-card ${isStarter ? 'subscribed' : ''}`}
                  >
                    {isStarter && <div className="subscribed-badge">MOST POPULAR</div>}

                    <div className="plan-header">
                      <h3 className="plan-name">{plan.name.toUpperCase()}</h3>

                      {plan.monthlyPrice === 0 ? (
                        <>
                          <div className="plan-price">
                            <span className="price-label">Free</span>
                              <span className="period">/Forever</span>
                          </div>
                          
                        </>
                      ) : (
                        <>
                          <div className="plan-price">
                            <span className="currency">₹</span>
                            <span className="amount">
                              {billingPeriod === 'monthly' 
                                ? plan.monthlyPrice.toFixed(2) 
                                : (plan.yearlyPrice / 12).toFixed(2)}
                            </span>
                            <span className="period">/Monthly</span>
                          </div>
                          {billingPeriod === 'yearly' && (
                            <p className="billed-yearly">₹{plan.yearlyPrice.toFixed(2)} billed annually</p>
                          )}
                        </>
                      )}
                    </div>


                    <h3 className="plan-description">
                    <span className="highlight">{plan.description}</span>  
                    </h3>

                    <ul className="plan-features">
                      {plan.features && plan.features.length > 0 ? (
                        plan.features.map((feature, idx) => (
                          <li key={idx} className={feature.isAvailable === false ? 'unavailable' : ''}>
                            <span className="check-icon">{feature.isAvailable !== false ? '✓' : '✗'}</span>
                            {feature.name}
                          </li>
                        ))
                      ) : (
                        <li>No features available</li>
                      )}
                    </ul>

                    <a 
                      href={`/signup?plan=${plan.id}`}
                      className={`btn-signup-free ${isBasic ? 'btn-basic' : isStarter ? 'btn-starter' : 'btn-growth'}`}
                      onClick={() => handlePlanSignupClick(`pricing_plan_${plan.name}`)}
                    >
                      Start building trust today
                    </a>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>


      <section className="pricing-faq">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <button
                  className={`faq-question ${openFaq === index ? 'active' : ''}`}
                  onClick={() => toggleFaq(index)}
                >
                  <span>{faq.question}</span>
                  <span className="faq-icon">{openFaq === index ? '−' : '+'}</span>
                </button>
                {openFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA Section */}

      <section className="pricing-cta">
        <div className="container">
          <div className="pricing-cta-panel">
            <div className="pricing-cta-copy">
              <span className="pricing-cta-eyebrow">Start free today</span>
              <h2>Turn customer feedback into proof that sells.</h2>
              <p>Create your branded collection page, share your first link, and publish customer stories in less than two minutes.</p>
              <div className="pricing-cta-points" aria-label="Signup benefits">
                <span>No credit card</span>
                <span>Setup in 2 minutes</span>
                <span>Upgrade anytime</span>
              </div>
            </div>
            <div className="pricing-cta-action">
              <div className="cta-form">
                <div className="cta-input-group">
                  <label htmlFor="pricing-cta-email">Work email</label>
                  <input
                    id="pricing-cta-email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) setEmailError('');
                    }}
                    className={emailError ? 'error' : ''}
                  />
                  {emailError && <span className="error-message">{emailError}</span>}
                </div>
                <button
                  onClick={handleSignup}
                  className="btn-primary"
                >
                  Start building trust
                </button>
              </div>
              <p className="cta-terms">
                Already a member? <Link to="/signup" onClick={() => trackLoginClick('pricing_cta')}>Sign in</Link>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Pricing;
